import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ContentReflectionModal.module.css';
import { saveContentReflection } from '../../utils/api';
import { fillTemplate, useContentCatalog } from '../../utils/contentCatalog';
import {
  calculateStreak,
  calculateWeeklyProgress,
  checkInScoreBarPercent,
  checkInScoreTrackPercent,
  DEFAULT_CHECK_IN_ANXIETY,
  DEFAULT_CHECK_IN_CONFIDENCE,
  generateMicroInsight,
  generateProgressSummary,
  MICRO_INSIGHT_EMOTIONS,
  CHECK_IN_SCORE_MAX,
  CHECK_IN_SCORE_MIN,
  getLocalDateString,
  upsertContentReflection,
} from '../../utils/checkInUtils';

const STEPS = {
  CHECKIN: 'checkin',
  REFLECT: 'reflect',
  COMPLETE: 'complete',
  PROGRESS: 'progress',
};

function PiggyBankIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6.25 12.25c-.15-3.35 2.65-5.85 6.1-6 2.95-.12 5.45 1.45 5.9 3.75.35 1.85-.35 3.35-1.45 4.05 1.05.55 1.95 1.65 2.1 3.15.2 1.85-1.25 3.55-3.95 3.85-3.35.3-6.6-1.05-7.05-4.35-.25-1.95.85-3.35 1.35-3.45z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <ellipse cx="16.1" cy="11.85" rx="2.15" ry="1.7" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="15.45" cy="11.6" r="0.32" fill="currentColor" />
      <circle cx="16.75" cy="11.6" r="0.32" fill="currentColor" />
      <path d="M10.25 7.35h3.25" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="10.35" cy="10.35" r="0.5" fill="currentColor" />
      <path
        d="M8.1 8.45c-.55-1.05.35-1.95 1.15-1.75"
        stroke="currentColor"
        strokeWidth="1.15"
        strokeLinecap="round"
      />
      <path
        d="M8.35 16.35v1.1M10.75 16.55v.95M13.35 16.55v.95M15.65 16.35v1.1"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path
        d="M5.85 12.85c-1.35-.35-1.95 1-1.55 2.15"
        stroke="currentColor"
        strokeWidth="1.15"
        strokeLinecap="round"
      />
    </svg>
  );
}

function DoubleCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12.5 9 16.5 18 7.5M8 12.5 12 16.5 21 7.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ConfidenceIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 4.5 13.35 9.15 18 10.5 13.35 11.85 12 16.5 10.65 11.85 6 10.5 10.65 9.15 12 4.5Z"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
      <path
        d="M5.75 15.25 6.55 17.65 8.95 18.45 6.55 19.25 5.75 21.65 4.95 19.25 2.55 18.45 4.95 17.65 5.75 15.25Z"
        stroke="currentColor"
        strokeWidth="1.15"
        strokeLinejoin="round"
        opacity="0.8"
      />
      <path
        d="M16.75 14.25 17.4 16.05 19.2 16.7 17.4 17.35 16.75 19.15 16.1 17.35 14.3 16.7 16.1 16.05 16.75 14.25Z"
        stroke="currentColor"
        strokeWidth="1.15"
        strokeLinejoin="round"
        opacity="0.8"
      />
    </svg>
  );
}

function AnxietyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="10" cy="9.25" r="3.35" stroke="currentColor" strokeWidth="1.35" />
      <path
        d="M5.75 17.5c1.15-2.2 2.7-3.25 4.25-3.25s3.1 1.05 4.25 3.25"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
      />
      <circle cx="17.25" cy="8.25" r="3.1" stroke="currentColor" strokeWidth="1.25" />
      <path
        d="M17.25 6.1c.9 0 1.45.6 1.45 1.25 0 .7-.6 1.05-1 1.35-.35.3-.45.55-.45.95"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <circle cx="17.25" cy="10.65" r="0.5" fill="currentColor" />
    </svg>
  );
}

function StarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2l2.9 6.3L22 9.3l-5 4.4 1.5 6.5L12 17.8 5.5 20.2 7 13.7 2 9.3l7.1-1z" />
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 18h6M10 21h4M8.5 14a5.5 5.5 0 1 1 7 0c-.9.8-1.5 1.7-1.5 3H10c0-1.3-.6-2.2-1.5-3Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3.25 13.55 8.45 18.75 10 13.55 11.55 12 16.75 10.45 11.55 5.25 10 10.45 8.45 12 3.25Z"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
      <path
        d="M18.65 15.05 19.25 16.75 20.95 17.35 19.25 17.95 18.65 19.65 18.05 17.95 16.35 17.35 18.05 16.75 18.65 15.05Z"
        stroke="currentColor"
        strokeWidth="1.15"
        strokeLinejoin="round"
        opacity="0.85"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.25" opacity="0.25" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="15.5" r="1" fill="currentColor" />
      <circle cx="12" cy="15.5" r="1" fill="currentColor" />
      <circle cx="16" cy="15.5" r="1" fill="currentColor" />
    </svg>
  );
}

function getSliderColor(value, invert = false) {
  const base = checkInScoreTrackPercent(value) / 100;
  const ratio = Math.max(0, Math.min(1, invert ? 1 - base : base));
  const r = Math.round(239 + (34 - 239) * ratio);
  const g = Math.round(68 + (197 - 68) * ratio);
  const b = Math.round(68 + (94 - 68) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

function rgbColorToRgba(rgbColor, alpha) {
  const match = rgbColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) return rgbColor;
  return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
}

function MetricSlider({
  label,
  icon,
  value,
  onChange,
  leftLabel,
  centerLabel,
  rightLabel,
  invertColor = false,
  animationDelay = 0,
}) {
  const sliderColor = getSliderColor(value, invertColor);
  const sliderTint = rgbColorToRgba(sliderColor, 0.12);
  const sliderRing = rgbColorToRgba(sliderColor, 0.22);

  return (
    <div
      className={styles.sliderCard}
      style={{
        '--slider-color': sliderColor,
        '--slider-tint': sliderTint,
        '--slider-ring': sliderRing,
        '--slider-progress': `${checkInScoreTrackPercent(value)}%`,
        animationDelay: `${animationDelay}ms`,
      }}
    >
      <div className={styles.sliderHeader}>
        <div className={styles.sliderLabelRow}>
          <div className={styles.sliderIcon}>{icon}</div>
          <span className={styles.sliderLabel}>{label}</span>
        </div>
        <span key={value} className={styles.sliderValue}>
          {value}
        </span>
      </div>
      <input
        type="range"
        min={CHECK_IN_SCORE_MIN}
        max={CHECK_IN_SCORE_MAX}
        step="1"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className={styles.rangeInput}
        aria-label={`${label} level`}
        aria-valuemin={CHECK_IN_SCORE_MIN}
        aria-valuemax={CHECK_IN_SCORE_MAX}
        aria-valuenow={value}
        aria-valuetext={`${value} out of ${CHECK_IN_SCORE_MAX}`}
      />
      <div className={styles.rangeLabels}>
        <span>{leftLabel}</span>
        <span>{centerLabel}</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  );
}

const INSIGHT_EMOTION_CLASS = {
  [MICRO_INSIGHT_EMOTIONS.CONFIDENCE]: styles.insightEmotionConfidence,
  [MICRO_INSIGHT_EMOTIONS.CALM]: styles.insightEmotionCalm,
  [MICRO_INSIGHT_EMOTIONS.BALANCE]: styles.insightEmotionBalance,
  [MICRO_INSIGHT_EMOTIONS.STREAK]: styles.insightEmotionStreak,
  [MICRO_INSIGHT_EMOTIONS.DEFAULT]: styles.insightEmotionDefault,
};

function MicroInsightIcon({ emotion }) {
  if (emotion === MICRO_INSIGHT_EMOTIONS.CONFIDENCE) return <ConfidenceIcon />;
  if (emotion === MICRO_INSIGHT_EMOTIONS.CALM) return <AnxietyIcon />;
  if (emotion === MICRO_INSIGHT_EMOTIONS.BALANCE) return <SparkleIcon />;
  if (emotion === MICRO_INSIGHT_EMOTIONS.STREAK) return <StarIcon />;
  return <LightbulbIcon />;
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return dateStr;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

const ContentReflectionModal = ({
  isOpen,
  contentId,
  contentTitle,
  reflections,
  onReflected,
  onClose,
}) => {
  const navigate = useNavigate();
  const { catalog } = useContentCatalog();
  const copy = catalog?.reflection || {};
  const [step, setStep] = useState(STEPS.CHECKIN);
  const [stepExiting, setStepExiting] = useState(false);
  const [completeActive, setCompleteActive] = useState(false);
  const [confidence, setConfidence] = useState(DEFAULT_CHECK_IN_CONFIDENCE);
  const [anxiety, setAnxiety] = useState(DEFAULT_CHECK_IN_ANXIETY);
  const [history, setHistory] = useState(reflections);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [progressAnim, setProgressAnim] = useState(0);
  const [summaryAnim, setSummaryAnim] = useState({
    streak: 0,
    weeklyPct: 0,
    totalCheckIns: 0,
  });
  const [showAllCheckIns, setShowAllCheckIns] = useState(false);

  const stepTransitionRef = useRef(null);
  const completeTapRef = useRef(null);

  const goToStep = useCallback((nextStep) => {
    if (stepTransitionRef.current) {
      window.clearTimeout(stepTransitionRef.current);
    }
    setStepExiting(true);
    stepTransitionRef.current = window.setTimeout(() => {
      setStep(nextStep);
      setStepExiting(false);
      stepTransitionRef.current = null;
    }, 120);
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (stepTransitionRef.current) {
        window.clearTimeout(stepTransitionRef.current);
        stepTransitionRef.current = null;
      }
      if (completeTapRef.current) {
        window.clearTimeout(completeTapRef.current);
        completeTapRef.current = null;
      }
      setStep(STEPS.CHECKIN);
      setStepExiting(false);
      setCompleteActive(false);
      setConfidence(DEFAULT_CHECK_IN_CONFIDENCE);
      setAnxiety(DEFAULT_CHECK_IN_ANXIETY);
      setHistory(reflections);
      setSaving(false);
      setSaveError('');
      setProgressAnim(0);
      setSummaryAnim({ streak: 0, weeklyPct: 0, totalCheckIns: 0 });
      setShowAllCheckIns(false);
    }
  }, [isOpen, reflections]);

  const streak = useMemo(() => calculateStreak(history), [history]);
  const microInsight = useMemo(() => generateMicroInsight(history), [history]);
  const weeklyProgress = useMemo(() => calculateWeeklyProgress(history), [history]);
  const progressSummary = useMemo(() => generateProgressSummary(history), [history]);

  useEffect(() => {
    if (step !== STEPS.COMPLETE) return undefined;
    setProgressAnim(0);
    let rafId = 0;
    const target = weeklyProgress;
    const startTimer = window.setTimeout(() => {
      const startTime = performance.now();
      const duration = 1100;
      const tick = (now) => {
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        setProgressAnim(target * eased);
        if (t < 1) rafId = window.requestAnimationFrame(tick);
      };
      rafId = window.requestAnimationFrame(tick);
    }, 900);
    return () => {
      window.clearTimeout(startTimer);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [step, weeklyProgress]);

  useEffect(() => {
    if (step !== STEPS.PROGRESS) return undefined;

    setSummaryAnim({
      streak: 0,
      weeklyPct: 0,
      totalCheckIns: 0,
    });

    let rafId = 0;
    const startTimer = window.setTimeout(() => {
      const targets = {
        streak: progressSummary.streak,
        weeklyPct: progressSummary.weeklyPct,
        totalCheckIns: progressSummary.totalCheckIns,
      };
      const startTime = performance.now();
      const duration = 900;

      const tick = (now) => {
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / duration);
        const eased = 1 - Math.pow(1 - t, 3);

        setSummaryAnim({
          streak: Math.round(targets.streak * eased),
          weeklyPct: Math.round(targets.weeklyPct * eased),
          totalCheckIns: Math.round(targets.totalCheckIns * eased),
        });

        if (t < 1) rafId = window.requestAnimationFrame(tick);
      };

      rafId = window.requestAnimationFrame(tick);
    }, 280);

    return () => {
      window.clearTimeout(startTimer);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [step, progressSummary]);

  if (!isOpen) return null;

  const handleCompleteTap = () => {
    setCompleteActive(true);
    if (completeTapRef.current) window.clearTimeout(completeTapRef.current);
    completeTapRef.current = window.setTimeout(() => {
      completeTapRef.current = null;
      goToStep(STEPS.REFLECT);
    }, 200);
  };

  const handleNextStep = async () => {
    setSaving(true);
    setSaveError('');
    const today = getLocalDateString();
    const result = await saveContentReflection({
      contentId,
      confidence,
      anxiety,
      reflectedOn: today,
    }, navigate);
    setSaving(false);

    if (!result?.ok) {
      setSaveError(result?.message || copy.save_error || '');
      return;
    }

    if (result.ok) {
      const updatedHistory = upsertContentReflection(history, {
        content_id: contentId,
        title: contentTitle,
        date: today,
        confidence,
        anxiety,
        completed: true,
      });
      setHistory(updatedHistory);
      if (result.content) onReflected?.(result.content);
      goToStep(STEPS.COMPLETE);
    }
  };

  const modalClassName = [
    styles.modal,
    step === STEPS.CHECKIN ? styles.modalCheckIn : '',
    step === STEPS.REFLECT ? styles.modalWide : '',
    step === STEPS.PROGRESS ? styles.modalProgress : '',
    step === STEPS.COMPLETE ? styles.modalComplete : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`${styles.overlay} ${step === STEPS.CHECKIN ? styles.overlayCheckIn : ''}`}
      role="presentation"
    >
      <div
        className={modalClassName}
        role="dialog"
        aria-modal="true"
        aria-label={
          step === STEPS.CHECKIN
            ? copy.checkin_aria
            : step === STEPS.REFLECT
              ? copy.reflect_aria
              : step === STEPS.COMPLETE
                ? copy.saved_aria
                : copy.summary_aria
        }
      >
        <div className={`${styles.stepContent} ${stepExiting ? styles.stepExiting : ''}`}>

        {step === STEPS.CHECKIN && (
          <>
            <div className={styles.checkInBody}>
              <div className={styles.checkInContent}>
                <h2 className={styles.title}>{copy.checkin_title}</h2>
                <p className={styles.subtitle}>{contentTitle}</p>
                <div className={styles.completeBtnWrap}>
                  <button
                    type="button"
                    className={`${styles.completeBtn} ${completeActive ? styles.completeBtnActive : ''}`}
                    onClick={handleCompleteTap}
                    aria-label={copy.tap_aria}
                  >
                    <DoubleCheckIcon />
                  </button>
                  <p className={styles.completeHint}>{copy.tap_to_reflect}</p>
                </div>
              </div>
            </div>
            <div className={styles.footer}>
              <div className={styles.streakBlock}>
                <span className={styles.streakLabel}>{copy.current_streak}</span>
                <span className={styles.streakValue}>
                  {streak} {streak === 1 ? copy.day : copy.days}
                </span>
              </div>
            </div>
          </>
        )}

        {step === STEPS.REFLECT && (
          <div className={styles.reflectBody}>
            <div className={styles.reflectHeader}>
              <div className={styles.reflectBadge}>
                <SparkleIcon />
                <span>{copy.reflect_step}</span>
              </div>
              <h2 className={styles.reflectTitle}>{copy.reflect_title}</h2>
              <p className={styles.reflectSubtitle}>
                {fillTemplate(copy.reflect_subtitle, { title: contentTitle })}
              </p>
            </div>
            <div className={styles.sliderStack}>
              <MetricSlider
                label={copy.confidence}
                icon={<ConfidenceIcon />}
                value={confidence}
                onChange={setConfidence}
                leftLabel={copy.confidence_low}
                centerLabel={copy.confidence_mid}
                rightLabel={copy.confidence_high}
                animationDelay={220}
              />
              <MetricSlider
                label={copy.anxiety}
                icon={<AnxietyIcon />}
                value={anxiety}
                onChange={setAnxiety}
                leftLabel={copy.anxiety_low}
                centerLabel={copy.anxiety_mid}
                rightLabel={copy.anxiety_high}
                invertColor
                animationDelay={320}
              />
            </div>
            <div className={styles.reflectActions}>
              <button
                type="button"
                className={`${styles.nextBtn} ${saving ? styles.nextBtnSaving : ''}`}
                onClick={handleNextStep}
                disabled={saving}
              >
                <span className={styles.nextBtnLabel}>{saving ? copy.saving : copy.next_step}</span>
                {saving ? (
                  <span className={styles.nextBtnSpinner}>
                    <SpinnerIcon />
                  </span>
                ) : (
                  <span className={styles.nextBtnArrow}>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M5 12h14M13 6l6 6-6 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </button>
              {saveError ? <p className={styles.saveError}>{saveError}</p> : null}
            </div>
          </div>
        )}

        {step === STEPS.COMPLETE && (
          <div className={styles.completeHero}>
            <div className={styles.starsWrap}>
              <span className={styles.starGlow} aria-hidden="true" />
              <span className={styles.starRing} aria-hidden="true" />
              <StarIcon className={`${styles.miniStar} ${styles.miniStar1}`} />
              <StarIcon className={`${styles.miniStar} ${styles.miniStar2}`} />
              <StarIcon className={`${styles.miniStar} ${styles.miniStar3}`} />
              <div className={styles.starCircle}>
                <StarIcon />
              </div>
            </div>
            <h2 className={styles.title}>{copy.saved_title}</h2>
            <p className={styles.subtitle}>
              {fillTemplate(copy.saved_subtitle, { title: contentTitle })}
            </p>
            <div
              key={microInsight.seed}
              className={[
                styles.insightCard,
                INSIGHT_EMOTION_CLASS[microInsight.emotion] || styles.insightEmotionDefault,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div className={styles.insightIcon}>
                <MicroInsightIcon emotion={microInsight.emotion} />
              </div>
              <div className={styles.insightContent}>
                <span className={styles.insightLabel}>{copy.micro_insight}</span>
                <p className={styles.insightText}>{microInsight.message}</p>
              </div>
            </div>
            <div className={styles.progressSection}>
              <div className={styles.progressHeader}>
                <span>{copy.days_reflected}</span>
                <span>{Math.round(progressAnim)}%</span>
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${progressAnim}%` }}
                />
              </div>
            </div>
            <div className={styles.actionStack}>
              <button type="button" className={styles.secondaryBtn} onClick={() => goToStep(STEPS.PROGRESS)}>
                {copy.view_progress}
              </button>
              <button type="button" className={styles.primaryBtn} onClick={onClose}>
                <span>{copy.close}</span>
              </button>
            </div>
          </div>
        )}

        {step === STEPS.PROGRESS && (
          <div className={styles.summaryBody}>
            <div className={styles.summaryHeader}>
              <div className={styles.summaryBadge}>
                <SparkleIcon />
                <span>{copy.summary_step}</span>
              </div>
              <h2 className={styles.summaryTitle}>{copy.summary_title}</h2>
              <p className={styles.summarySubtitle}>{copy.summary_subtitle}</p>
            </div>

            <div className={styles.summaryGrid}>
              <div className={`${styles.summaryStat} ${styles.summaryStatStreak}`}>
                <div className={styles.summaryStatIcon}>
                  <StarIcon />
                </div>
                <span className={styles.summaryStatLabel}>{copy.streak_label}</span>
                <span className={styles.summaryStatValue}>
                  {summaryAnim.streak} {summaryAnim.streak === 1 ? copy.day : copy.days}
                </span>
              </div>
              <div className={`${styles.summaryStat} ${styles.summaryStatWeekly}`}>
                <div className={styles.summaryStatIcon}>
                  <DoubleCheckIcon />
                </div>
                <span className={styles.summaryStatLabel}>{copy.weekly_progress}</span>
                <span className={styles.summaryStatValue}>{summaryAnim.weeklyPct}%</span>
                <div className={styles.summaryStatBar}>
                  <div
                    className={styles.summaryStatFill}
                    style={{ width: `${summaryAnim.weeklyPct}%` }}
                  />
                </div>
              </div>
              <div className={`${styles.summaryStat} ${styles.summaryStatTotal}`}>
                <div className={styles.summaryStatIcon}>
                  <CalendarIcon />
                </div>
                <span className={styles.summaryStatLabel}>{copy.reflections}</span>
                <span className={styles.summaryStatValue}>{summaryAnim.totalCheckIns}</span>
              </div>
            </div>

            {progressSummary.allCheckIns.length > 0 ? (
              <div className={styles.recentSection}>
                <h3 className={styles.recentHeading}>{copy.recent}</h3>
                <ul className={styles.recentList}>
                  {(showAllCheckIns ? progressSummary.allCheckIns : progressSummary.last3).map((entry, index) => (
                    <li
                      key={entry.content_id || `${entry.date}-${index}`}
                      className={styles.recentItem}
                      style={{ animationDelay: `${520 + index * 75}ms` }}
                    >
                      <div className={styles.recentMain}>
                        <div className={styles.recentDateRow}>
                          <span className={styles.recentDate}>
                            {entry.title ? `${entry.title} · ` : ''}
                            {formatDisplayDate(entry.date)}
                          </span>
                          {entry.date === getLocalDateString() && (
                            <span className={styles.todayBadge}>{copy.today}</span>
                          )}
                        </div>
                        <div className={styles.recentMetricBars}>
                          <div className={styles.recentMetric}>
                            <span className={styles.recentMetricLabel}>{copy.confidence_short}</span>
                            <div className={styles.recentMetricTrack}>
                              <div
                                className={styles.recentMetricFill}
                                style={{
                                  width: `${checkInScoreBarPercent(entry.confidence)}%`,
                                  '--metric-color': getSliderColor(entry.confidence),
                                }}
                              />
                            </div>
                            <span className={styles.recentMetricValue}>{entry.confidence}</span>
                          </div>
                          <div className={styles.recentMetric}>
                            <span className={styles.recentMetricLabel}>{copy.anxiety_short}</span>
                            <div className={styles.recentMetricTrack}>
                              <div
                                className={styles.recentMetricFill}
                                style={{
                                  width: `${checkInScoreBarPercent(entry.anxiety)}%`,
                                  '--metric-color': getSliderColor(entry.anxiety, true),
                                }}
                              />
                            </div>
                            <span className={styles.recentMetricValue}>{entry.anxiety}</span>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                {progressSummary.allCheckIns.length > 3 ? (
                  <div className={styles.recentMoreWrap}>
                    <button
                      type="button"
                      className={styles.checkMoreLink}
                      onClick={() => setShowAllCheckIns((prev) => !prev)}
                      aria-expanded={showAllCheckIns}
                    >
                      {showAllCheckIns ? copy.check_less : copy.check_more}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={styles.summaryEmpty}>
                <div className={styles.summaryEmptyIcon}>
                  <PiggyBankIcon />
                </div>
                <p className={styles.summaryEmptyText}>
                  {copy.empty_history}
                </p>
              </div>
            )}

            <div className={styles.summaryActions}>
              <button type="button" className={styles.primaryBtn} onClick={onClose}>
                <span>{copy.close}</span>
              </button>
            </div>
          </div>
        )}

        </div>
      </div>
    </div>
  );
};

export default ContentReflectionModal;
